import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { BehaviorSubject, Observable } from 'rxjs'
import { UserProfileService } from '../../../../../../framework/authentication/services/user-profile.service'
import { filter, mergeMap, pluck, tap } from 'rxjs/operators'
import { DealConfigurationQuery } from '../../../../../../generated/graphql'
import { ConfigurationRouteService } from '../../services/configurationRoute.service'
import { DealMutationService } from 'modules/deals/services/dealMutation.service'
import { DealQueryService } from 'modules/deals/services/dealQuery.service'
import { TenantService } from 'framework/tenant/tenant.service'
import { ConfigurationAnalyticsService } from '../../services/configurationAnalytics.service'
import { LesseeCompany } from 'modules/deals/utilities/deal-types'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { HomeUrlService } from '../../../../../../framework/home-url/home-url.service'

@UntilDestroy()
@Component({
    selector: 'mg-confirm',
    templateUrl: './confirm.component.html',
    styleUrls: ['./confirm.component.scss'],
})
export class ConfirmComponent implements OnInit {
    isSubmitting: boolean
    hasSignedRequirement$: Observable<boolean>
    hasSignedRequirement: boolean
    needsSigningPerson$: Observable<boolean>
    needsSigningPerson: boolean
    deal$: Observable<DealConfigurationQuery['Deal']>
    deal: DealConfigurationQuery['Deal']
    canSubmit: boolean = false
    dealId: string
    layoutTrigger$ = new BehaviorSubject(true)
    hasCheckout: boolean
    needsChamberOfCommerceNumber: boolean

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private userProfileService: UserProfileService,
        private dealMutationService: DealMutationService,
        private dealQueryService: DealQueryService,
        private configurationRouteService: ConfigurationRouteService,
        private tenantService: TenantService,
        private configurationAnalyticsService: ConfigurationAnalyticsService,
        private homeUrlService: HomeUrlService
    ) {
        this.hasCheckout = this.tenantService.hasCheckout$.getValue()
        this.needsChamberOfCommerceNumber = this.tenantService.needsChamberOfCommerceNumber$.getValue()
    }

    ngOnInit() {
        // get configuration Id from route data
        const dealId$: Observable<string> = this.route.parent.data.pipe(
            untilDestroyed(this),
            pluck('configurationId'),
            filter(Boolean),
            tap<string>(dealId => (this.dealId = dealId))
        )

        // get ProductConfiguration
        this.deal$ = dealId$.pipe(
            untilDestroyed(this),
            mergeMap(dealId => this.dealQueryService.watchDealConfiguration(dealId))
        )

        // store local copy
        this.deal$.pipe(untilDestroyed(this)).subscribe((deal: DealConfigurationQuery['Deal']) => {
            this.deal = deal

            this.validateDeal(deal)
        })
    }

    validateDeal(deal) {
        if (!deal.requirements.isCompanyRequired && !deal.requirements.isDriverRequired) {
            return
        }

        const companyPredicate =
            !deal.requirements.isCompanyRequired ||
            (deal.lessee && deal.lessee.__typename === 'LesseeCompany' && this.validateCompany(deal.lessee))
        const driverPredicate = !deal.requirements.isDriverRequired || (deal.driver && deal.driver.emailAddress)

        this.canSubmit = companyPredicate && driverPredicate
    }

    validateCompany(lesseeCompany: LesseeCompany): boolean {
        if (!this.hasCheckout) {
            return !!lesseeCompany?.name
        }

        return (
            (!this.needsChamberOfCommerceNumber || !!lesseeCompany?.chamberOfCommerceNr) &&
            !!lesseeCompany?.name &&
            !!lesseeCompany?.postalAddress?.street &&
            !!lesseeCompany?.postalAddress?.houseNumber &&
            !!lesseeCompany?.postalAddress?.zipcode &&
            !!lesseeCompany?.postalAddress?.city
        )
    }

    async onSubmit() {
        if (!this.canSubmit || this.isSubmitting) {
            return
        }

        this.isSubmitting = true

        if (!this.deal.requirements.isCompanyRequired) {
            await this.userProfileService.persistUserProfile()
        }

        this.configurationRouteService.routeToAvailableRouteEnabled = false

        this.configurationAnalyticsService.push({
            dealId: this.dealId,
            name: 'carconfigurator.funnel.submit',
        })

        if (!this.hasCheckout) {
            // we are in a flow that doesn't lead to the order dashboard
            this.dealMutationService.makeDealOffer(this.dealId).then(() => {
                this.router.navigate(['../celebrate'], { relativeTo: this.route })
            })
        } else {
            // we are in a flow that does lead to the order dashboard

            this.dealMutationService.makeDealOffer(this.dealId).then(() => {
                window.location.assign(`${this.homeUrlService.getHomeUrl()}/account/offer/${this.dealId}`)
            })
        }
    }

    onUserProfileChange(hasFullAddress) {
        if (!this.deal.requirements.isCompanyRequired && !this.hasCheckout) {
            this.canSubmit = true
        } else if (hasFullAddress && !this.deal.requirements.isCompanyRequired) {
            this.canSubmit = true
        } else if (!hasFullAddress && !this.deal.requirements.isCompanyRequired) {
            this.canSubmit = false
        }
    }
}
